import React, { useEffect, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@material-ui/core'
import { assets, DatePicker, Input, SelectOption, SelectSearch } from '@pulseops/common'
import { pipe } from 'fp-ts/function'
import { ZIO } from '@mxt/zio'
import { View } from 'react-native'
import { PolicyDispatchingService } from '../services/PolicyDispatchingService'
import { usePolicyDispatchingOffice } from './PolicyDispatchingOfficeContext'
import {
  borderedDatePickerContainerStyle,
  borderedInputStyle,
  borderedSelectSearchStyle
} from '../styles/policyDispatchingStyles'
import { isDateAfterString, isDateAtLeastDaysAfterString } from '../utils/policyDispatchingUtils'

type Props = {
  visible: boolean
  selectedCount: number
  currentDates?: Array<string | null | undefined>
  stockInDates?: Array<string | null | undefined>
  minimumDaysAfterStockInDate?: number
  action: 'changeDate' | 'changeOffice'
  title?: string
  dateLabel?: string
  duplicateDateMessage?: string
  stockInDateMessage?: string
  onClose: () => void
  onConfirm: (value: Date | SelectOption, remark: string) => void
}

const formatDate = (date: Date | null) => {
  if (!date) {
    return null
  }

  const day = `${date.getDate()}`.padStart(2, '0')
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const year = date.getFullYear()

  return `${day}/${month}/${year}`
}

export default function PDChangeInfoDialog({
  visible,
  selectedCount,
  currentDates = [],
  stockInDates = [],
  minimumDaysAfterStockInDate,
  action,
  title,
  dateLabel = 'Ngày nhập kho mới',
  duplicateDateMessage = 'Ngày nhập kho mới không được trùng với ngày nhập kho hiện tại.',
  stockInDateMessage = 'Ngày giao mới phải lớn hơn hoặc bằng ngày nhập kho.',
  onClose,
  onConfirm
}: Props) {
  const { officeCode } = usePolicyDispatchingOffice()
  const [newDate, setNewDate] = useState<Date | null>(null)
  const [newOffice, setNewOffice] = useState<SelectOption | null>(null)
  const [officeOptions, setOfficeOptions] = useState<SelectOption[]>([])
  const [remark, setRemark] = useState('')
  const selectedStockInDateValues = currentDates.filter(Boolean)
  const isDuplicatedStockInDate =
    action === 'changeDate' && Boolean(newDate) && selectedStockInDateValues.includes(formatDate(newDate) || '')
  const isNotAfterStockInDate =
    action === 'changeDate' &&
    Boolean(newDate) &&
    stockInDates.some((stockInDate) =>
      minimumDaysAfterStockInDate === undefined
        ? !isDateAfterString(newDate, stockInDate)
        : !isDateAtLeastDaysAfterString(newDate, stockInDate, minimumDaysAfterStockInDate)
    )

  useEffect(() => {
    if (action !== 'changeOffice' || !visible) {
      return
    }

    pipe(
      PolicyDispatchingService.getOffices,
      ZIO.map((response) =>
        response.body.items.map((item) => ({
          value: item.officeCode,
          label: item.officeCode
        }))
      ),
      ZIO.tap((options) =>
        ZIO.effectTotal(() => setOfficeOptions(options.filter((option) => option.value !== officeCode)))
      ),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          console.error('Unable to load offices for stock-in update', error)
        })
      ),
      ZIO.unsafeRun({})
    )
  }, [action, visible])

  const handleClose = () => {
    setNewDate(null)
    setNewOffice(null)
    setRemark('')
    onClose()
  }

  const handleConfirm = () => {
    const value = action === 'changeDate' ? newDate : newOffice
    if (!value || isDuplicatedStockInDate || isNotAfterStockInDate) {
      return
    }

    onConfirm(value, remark)
    handleClose()
  }

  return (
    <Dialog open={visible} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography style={styles.titleWrapper}>
        <strong>{title || (action === 'changeDate' ? 'Điều chỉnh ngày nhập kho' : 'Điều chỉnh văn phòng')}</strong>
        <IconButton aria-label="Đóng" onClick={handleClose} size="small">
          <assets.OBCallOtherClose />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {action === 'changeDate' ? (
          <View style={styles.field}>
            <DatePicker
              label={dateLabel}
              value={newDate}
              onChange={setNewDate}
              maxDate={undefined}
              minDate={undefined}
              containerStyle={borderedDatePickerContainerStyle}
              minDateMessage=""
              errorMessage={
                isDuplicatedStockInDate ? duplicateDateMessage : isNotAfterStockInDate ? stockInDateMessage : ''
              }
              hideUnderline
              required
            />
          </View>
        ) : (
          <View style={styles.field}>
            <SelectSearch
              label={'Văn phòng mới'}
              options={officeOptions}
              value={newOffice}
              onChange={setNewOffice}
              inputStyle={borderedSelectSearchStyle}
              disableUnderline
              isShowFullText={true}
            />
          </View>
        )}
        <View style={styles.field}>
          <Input
            title={'Số lượng hợp đồng'}
            value={selectedCount.toString()}
            inputStyle={borderedInputStyle}
            filled
            maxLength={1000}
            disabled
            alwayShowUnderline
          />
        </View>
        <View style={styles.field}>
          <Input
            title={'Ghi chú'}
            value={remark}
            inputStyle={{ ...borderedInputStyle, paddingTop: 10 }}
            filled
            maxLength={10000}
            onChange={setRemark}
            numberOfLines={5}
            multiline
          />
        </View>
      </DialogContent>
      <DialogActions style={styles.actions}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={action === 'changeDate' ? !newDate || isDuplicatedStockInDate || isNotAfterStockInDate : !newOffice}
        >
          Xác nhận
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const styles: Record<string, any> = {
  titleWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: 14,
    fontSize: 18
  },
  field: {
    marginTop: 16
  },
  actions: {
    justifyContent: 'center',
    padding: '12px 24px 20px'
  }
}
